/**
 * Admin Support Tickets API
 * Base: /api/v1/admin/support
 */
import api from './api';

const SUPPORT_BASE = '/admin/support';

export interface TicketMerchant {
  id: number;
  name?: string;
  phone?: string;
  storeName?: string;
}

export interface TicketAssignedAdmin {
  id: number;
  name?: string;
}

export interface TicketSummary {
  id: string;
  ticketCode: string;
  subject: string;
  status: string;
  priority: string;
  category: string;
  source?: 'MERCHANT' | 'USER';
  merchant: TicketMerchant;
  lastMessagePreview?: string | null;
  lastMessageAt?: string | null;
  merchantUnreadCount: number;
  adminUnreadCount: number;
  assignedAdmin?: TicketAssignedAdmin | null;
  createdAt?: string | null;
}

export interface SupportAttachment {
  id: string;
  fileType: string;
  fileName?: string;
  mimeType?: string;
  fileSize?: number;
  url: string;
  thumbUrl?: string | null;
}

export interface SupportMessage {
  id: string;
  senderType: string;
  senderName?: string;
  message: string;
  attachments: SupportAttachment[];
  createdAt?: string | null;
  readAt?: string | null;
}

export interface TicketDetailInfo {
  id: string;
  ticketCode: string;
  subject: string;
  description: string;
  status: string;
  priority: string;
  category: string;
  source?: 'MERCHANT' | 'USER';
  relatedOrderId?: number | null;
  merchant: TicketMerchant;
  assignedAdmin?: TicketAssignedAdmin | null;
  lastMessageAt?: string | null;
  lastMessageBy?: string | null;
  merchantUnreadCount: number;
  adminUnreadCount: number;
  createdAt?: string | null;
  updatedAt?: string | null;
}

export interface TicketDetail {
  ticket: TicketDetailInfo;
  messages: SupportMessage[];
  reportedUserContext?: ReportedUserContext | null;
}

export interface ReportedUserContext {
  id: number;
  name?: string;
  email?: string;
  phone?: string;
  createdAt?: string | null;
  isActive?: boolean;
  accountRestrictedUntil?: string | null;
  codRestrictedUntil?: string | null;
  totalOrders: number;
  cancelledOrders: number;
  deliveredOrders: number;
  codOrders: number;
  codFailedLast24h: number;
}

export interface TicketsListResponse {
  success: boolean;
  data?: {
    tickets: TicketSummary[];
    pagination: {
      page: number;
      limit: number;
      total: number;
      pages: number;
    };
  };
  message?: string;
}

export interface TicketDetailResponse {
  success: boolean;
  data?: TicketDetail;
  message?: string;
}

export interface TicketEvent {
  id: number;
  ticket_id: string;
  event_type: string;
  old_value?: string | null;
  new_value?: string | null;
  actor_type: string;
  actor_id?: number | null;
  created_at: string;
}

/** GET /admin/support/tickets */
export async function fetchTickets(params: {
  status?: string;
  priority?: string;
  category?: string;
  assignee?: string; // 'me' | 'unassigned' | admin id
  source?: 'MERCHANT' | 'USER';
  q?: string;
  page?: number;
  limit?: number;
}): Promise<TicketsListResponse> {
  const { assignee, source, ...rest } = params;
  const query: Record<string, string | number | undefined> = { ...rest };
  if (assignee !== undefined && assignee !== '') query.assignee = assignee;
  if (source != null) query.source = source;
  const res = await api.get<TicketsListResponse>(`${SUPPORT_BASE}/tickets`, {
    params: query,
  });
  return res.data;
}

/** GET /admin/support/tickets/:id */
export async function fetchTicketDetail(
  ticketId: string
): Promise<TicketDetailResponse> {
  const res = await api.get<TicketDetailResponse>(
    `${SUPPORT_BASE}/tickets/${ticketId}`
  );
  return res.data;
}

/** PATCH /admin/support/tickets/:id */
export async function patchTicket(
  ticketId: string,
  body: {
    status?: string;
    priority?: string;
    assigned_admin_id?: number | null;
  }
): Promise<{ success: boolean; data?: unknown; message?: string }> {
  const res = await api.patch(`${SUPPORT_BASE}/tickets/${ticketId}`, body);
  return res.data;
}

/** POST /admin/support/tickets/:id/messages */
export async function postMessage(
  ticketId: string,
  body: {
    message: string;
    attachment_urls?: Array<{
      url: string;
      file_name: string;
      mime_type: string;
      file_size: number;
      file_type?: string;
    }>;
  }
): Promise<{ success: boolean; data?: SupportMessage; message?: string }> {
  const res = await api.post(
    `${SUPPORT_BASE}/tickets/${ticketId}/messages`,
    body
  );
  return res.data;
}

/** POST /admin/support/tickets/:id/read */
export async function markTicketRead(ticketId: string): Promise<void> {
  await api.post(`${SUPPORT_BASE}/tickets/${ticketId}/read`);
}

/** POST /admin/support/attachments (multipart) */
export async function uploadAttachment(file: File): Promise<{
  url: string;
  file_name: string;
  mime_type: string;
  file_size: number;
  file_type: string;
}> {
  const formData = new FormData();
  formData.append('file', file);
  const res = await api.post<{
    success: boolean;
    data: {
      url: string;
      file_name: string;
      mime_type: string;
      file_size: number;
      file_type: string;
    };
  }>(`${SUPPORT_BASE}/attachments`, formData);
  if (!res.data.success || !res.data.data) {
    throw new Error(
      (res.data as { message?: string }).message || 'Upload failed'
    );
  }
  return res.data.data;
}
